import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import Lightning from './ui/Lightning';

const stats = [
  { label: 'Tracks Released', value: '50+' },
  { label: 'Monthly Listeners', value: '10K+' },
  { label: 'Countries Reached', value: '80+' },
  { label: 'AI Generations', value: '∞' },
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="about" className="relative py-24 md:py-32 bg-cyber-dark overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div 
          className="absolute top-1/2 left-0 w-[600px] h-[600px] -translate-y-1/2 -translate-x-1/2 rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, transparent 70%)',
          }}
        />
        <div 
          className="absolute top-1/4 right-0 w-[400px] h-[400px] translate-x-1/2 rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.6) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Lightning Effects - Cyan/Purple theme, less frequent */}
      <Lightning 
        interval={1200}
        probability={0.6}
        colors={['#00d4ff', '#8b5cf6', '#6366f1']}
        multiBolts={false}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Character Image - Epic Presentation */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <div className="relative">
              {/* Multi-layered glow effects */}
              <motion.div 
                className="absolute inset-[-15%] rounded-full blur-3xl"
                animate={{
                  opacity: [0.15, 0.3, 0.15],
                }}
                transition={{ duration: 4, repeat: Infinity }}
                style={{
                  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, rgba(0, 212, 255, 0.3) 50%, transparent 70%)',
                }}
              />
              
              {/* Geometric frame */}
              <div className="relative">
                {/* Corner accents */}
                <motion.div 
                  className="absolute -top-3 -left-3 w-12 h-12 border-l-2 border-t-2 border-cyber-cyan"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <motion.div 
                  className="absolute -top-3 -right-3 w-12 h-12 border-r-2 border-t-2 border-cyber-purple"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                />
                <motion.div 
                  className="absolute -bottom-3 -left-3 w-12 h-12 border-l-2 border-b-2 border-cyber-pink"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                />
                <motion.div 
                  className="absolute -bottom-3 -right-3 w-12 h-12 border-r-2 border-b-2 border-cyber-cyan"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                />

                {/* Image container with glass effect */}
                <div className="relative glass-card rounded-2xl overflow-hidden p-3">
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-cyber-purple/10 via-transparent to-cyber-cyan/10">
                    {/* Character Image */}
                    <motion.div
                      className="relative w-full h-full flex items-center justify-center"
                      animate={isInView ? {
                        y: [0, -8, 0],
                      } : {}}
                      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <img
                        src="/images/mascot-about.png"
                        alt="White Rabbit Bangers Character"
                        className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(139,92,246,0.4)]"
                      />
                    </motion.div>
                    
                    {/* Scan line effect */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(transparent 50%, rgba(0, 212, 255, 0.03) 50%)',
                        backgroundSize: '100% 4px',
                      }}
                    />
                    
                    {/* Subtle vignette */}
                    <div className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'radial-gradient(circle, transparent 40%, rgba(10, 10, 26, 0.4) 100%)',
                      }}
                    />
                  </div>

                  {/* Electric border effect */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2"
                    animate={{
                      borderColor: [
                        'rgba(0, 212, 255, 0.4)',
                        'rgba(139, 92, 246, 0.5)',
                        'rgba(236, 72, 153, 0.4)',
                        'rgba(0, 212, 255, 0.4)',
                      ],
                      boxShadow: [
                        '0 0 20px rgba(0, 212, 255, 0.2)',
                        '0 0 30px rgba(139, 92, 246, 0.3)',
                        '0 0 20px rgba(236, 72, 153, 0.2)',
                        '0 0 20px rgba(0, 212, 255, 0.2)',
                      ],
                    }}
                    transition={{ duration: 6, repeat: Infinity }}
                  />
                </div>
              </div>

              {/* Floating decorative elements */}
              <motion.div
                className="absolute -top-6 -right-6 w-20 h-20 border-2 border-cyber-cyan/30 rounded-lg"
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 4, repeat: Infinity },
                }}
              />
              <motion.div
                className="absolute -bottom-8 -left-8 w-16 h-16 border-2 border-cyber-purple/30 rounded-full"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                className="absolute top-1/2 -right-4 w-3 h-3 bg-cyber-pink rounded-full"
                animate={{
                  y: [-20, 20, -20],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                className="absolute top-1/3 -left-4 w-2 h-2 bg-cyber-cyan rounded-full"
                animate={{
                  y: [10, -10, 10],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="font-orbitron text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">ABOUT </span>
              <span className="bg-gradient-to-r from-cyber-cyan to-cyber-purple bg-clip-text text-transparent">
                THE RABBIT
              </span>
            </h2>

            <div className="space-y-4 text-white/70 font-rajdhani text-lg leading-relaxed">
              <p>
                <span className="text-cyber-cyan font-semibold">White Rabbit Bangers</span> emerged from the 
                digital underground—a fusion of artificial intelligence and raw sonic energy. 
                Born at the crossroads of trap, hip-hop, pop, country, rock, and beyond, the Rabbit 
                delivers bangers that transcend genre boundaries and blur the line between human and machine.
              </p>
              <p>
                <span className="text-cyber-purple/80 font-mono text-sm">Wake up, Neo...</span> Each track is a 
                red pill—a journey down the rabbit hole through neon-lit streets and digital landscapes. 
                The signature geometric hoodie represents the fractured reality we live in—colorful, 
                chaotic, and undeniably alive.
              </p>
              <p>
                This isn&apos;t just music. It&apos;s a 
                <span className="text-cyber-purple font-semibold"> glitch in the matrix</span>. 
                You followed the white rabbit here. Now see how deep the hole goes. The future sounds 
                <span className="text-cyber-pink font-semibold"> different</span>.
              </p>
            </div>

            {/* Stats Grid */}
            <motion.div
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="glass-card p-4 rounded-xl text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.05,
                    borderColor: 'rgba(0, 212, 255, 0.5)',
                  }}
                >
                  <div className="font-orbitron text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyber-cyan to-cyber-purple bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-white/50 text-xs md:text-sm font-rajdhani mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

